// Firebase Analytics & Google Analytics 4 Utility Functions
import { logEvent, setUserId as setFirebaseUserId, setUserProperties } from 'firebase/analytics';

let analyticsInstance = null;

// Initialize Firebase Analytics (called from App.js)
export const initAnalytics = (analytics) => {
  if (analytics && typeof window !== 'undefined') {
    analyticsInstance = analytics;
    return true;
  }
  return false;
};

// Track page views
export const trackPageView = (path) => {
  if (analyticsInstance) {
    logEvent(analyticsInstance, 'page_view', {
      page_path: path,
      page_title: document.title,
    });
  }
  // Also track via gtag for GA4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-BXD26N5CNQ', {
      page_path: path,
    });
  }
};

// Track events
export const trackEvent = (eventName, eventParams = {}) => {
  if (analyticsInstance) {
    logEvent(analyticsInstance, eventName, eventParams);
  }
  // Also track via gtag for GA4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Predefined event tracking functions
export const analytics = {
  // Authentication events
  login: (method = 'email') => {
    trackEvent('login', { method });
  },

  signup: (method = 'email') => {
    trackEvent('sign_up', { method });
  },

  logout: () => {
    trackEvent('logout');
  },

  // Conversion events
  beginCheckout: () => {
    trackEvent('begin_checkout');
  },

  purchase: (value, currency = 'USD') => {
    trackEvent('purchase', {
      value,
      currency,
    });
  },

  // Feature usage
  resumeCreated: () => {
    trackEvent('resume_created');
  },

  resumeExported: (format) => {
    trackEvent('resume_exported', { format });
  },

  aiResumeGenerated: () => {
    trackEvent('ai_resume_generated');
  },

  aiCoverLetterGenerated: () => {
    trackEvent('ai_cover_letter_generated');
  },

  atsAnalysisRun: () => {
    trackEvent('ats_analysis_run');
  },

  atsScoreReceived: (score) => {
    trackEvent('ats_score_received', { score });
  },

  aiSuggestionsRequested: () => {
    trackEvent('ai_suggestions_requested');
  },

  templateSelected: (templateName) => {
    trackEvent('template_selected', { template_name: templateName });
  },

  // Navigation events
  viewDashboard: () => {
    trackEvent('view_dashboard');
  },

  viewPricing: () => {
    trackEvent('view_pricing');
  },

  clickUpgradeButton: (location) => {
    trackEvent('click_upgrade_button', { button_location: location });
  },

  // Subscription events
  subscriptionStarted: (plan = 'premium') => {
    trackEvent('subscription_started', { plan });
  },

  subscriptionCanceled: () => {
    trackEvent('subscription_canceled');
  },

  // Error tracking
  error: (errorMessage, errorLocation) => {
    trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      location: errorLocation,
    });
  },

  // Custom event
  custom: (eventName, params = {}) => {
    trackEvent(eventName, params);
  },
};

// Set user properties
export const setUserProperty = (property, value) => {
  if (analyticsInstance) {
    setUserProperties(analyticsInstance, { [property]: value });
  }
  // Also set via gtag for GA4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', { [property]: value });
  }
};

// Set user ID for cross-device tracking
export const setUserId = (userId) => {
  if (analyticsInstance) {
    setFirebaseUserId(analyticsInstance, userId);
  }
  // Also set via gtag for GA4 if available
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-BXD26N5CNQ', {
      user_id: userId,
    });
  }
};

